import { CSSProperties, useCallback, useEffect } from 'react'
import { Typography, styled, useTheme, alpha } from '@mui/material'
import { VideoMessage } from '@weblite/ts-action/message'
import { ActionHandler } from '$common/action-handler'
import { PlayIcon } from '$common/icons/play'
import { useContentFileStatus } from '$common/react-hooks/message'
import { formatFileSize, useFileObjectUrl } from '$func/file'
import { formatDuration } from '$func/time'
import { showMediaViewerModal } from '$comp/modals/media-viewer'
import { DownloadIcon } from '$common/icons'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { useAsyncFunction } from '$helper/functions/async-function.react-hook'
import { getUploadingFile } from '$logic/persist-uploads'

const VIDEO_MAX_WIDTH = 280

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  overflow: hidden;
  padding: 4px;
`

const Thumbnail = styled('img')`
  object-fit: contain;
  background-color: black;
  &[data-apply-filter='true'] {
    filter: blur(10px);
  }
  transform: scale(1.05);
  display: block;
`

const InfoContainer = styled('div')`
  display: flex;
  overflow: hidden;
  align-items: center;
  padding: 4px 8px;
  position: absolute;
  /* @noflip */
  right: 8px;
  top: 8px;
  gap: 8px;
  cursor: pointer;

  background-color: ${({ theme }) => alpha(theme.palette.neutral1000, 0.6)};
  border-radius: 12px;
  flex-wrap: wrap;
`

const InfoTexts = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const InfoTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.neutral000};
  ${({ theme }) => ({ ...theme.typography.caption2 })}
`

export function Video({
  attachment,
  statusComp,
  uploadId,
  rootStyle = undefined,
  roomId,
  iid,
  shouldGetHighQualityThumbnail,
  thumbnailUploadId,
  id,
}: {
  attachment: VideoMessage['attachment']
  statusComp: JSX.Element | null
  uploadId: string
  rootStyle?: CSSProperties
  roomId: string
  iid: number
  shouldGetHighQualityThumbnail?: boolean
  thumbnailUploadId?: string
  id: string
}) {
  const theme = useTheme()
  const {
    fileId,
    width,
    duration,
    base64,
    size,
    height,
    thumbnailFileId,
  } = attachment
  const videoDurationText = formatDuration(duration, 's')
  const calculatedWidth = Math.min(width, VIDEO_MAX_WIDTH)
  const calculatedHeight = (calculatedWidth / width) * height

  const {
    state,
    percentage,
    cancelOperation,
    startDownload,
    conversionPercentage,
  } = useContentFileStatus({
    fileId,
    uploadId,
    messageType: 'VIDEO',
    hasConversion: true,
    messageId: id,
  })

  const {
    state: thumbnailState,
    startDownload: downloadThumbnail,
    blobURL: thumbnailBlobUrl,
  } = useContentFileStatus({
    uploadId: thumbnailUploadId || '',
    fileId: thumbnailFileId || '',
    messageType: 'IMAGE',
  })

  useEffect(() => {
    if (
      shouldGetHighQualityThumbnail &&
      thumbnailState === 'NOT_AVAILABLE' &&
      thumbnailFileId
    ) {
      downloadThumbnail()
    }
  }, [
    shouldGetHighQualityThumbnail,
    thumbnailState,
    downloadThumbnail,
    thumbnailFileId,
  ])

  const { value: uploadingThumbnail } = useAsyncFunction(async () => {
    if (state !== 'UPLOADING' && state !== 'CONVERTING') return null
    return getUploadingFile(thumbnailUploadId)
  }, [state])

  const uploadingVideoThumbnailUrl = useFileObjectUrl(uploadingThumbnail)

  const isInProgress =
    state === 'UPLOADING' || state === 'DOWNLOADING' || state === 'CONVERTING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(
    size,
    undefined,
    false,
  )
  const {
    sizeText: progressedSizeText,
    unit: progressSizeUnit,
  } = formatFileSize(Math.round(size * (percentage / 100)), fileSizeUnit, false)

  const playVideo = useCallback(async () => {
    showMediaViewerModal({ roomId, iid })
  }, [roomId, iid])

  return (
    <>
      <Container style={rootStyle}>
        <ActionHandler
          size={50}
          state={state}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={playVideo}
          onCustomActionIconClick={playVideo}
          customDownloadIconComp={
            <PlayIcon sx={{ color: 'white', width: '24px', height: '24px' }} />
          }
          customActionIconComp={
            <PlayIcon sx={{ color: 'white', width: '24px', height: '24px' }} />
          }
          rootSx={{
            position: 'absolute',
            /* @noflip */
            left: 'calc(50% - 25px)',
            top: 'calc(50% - 25px)',
            zIndex: 1,
          }}
          sx={{
            backgroundColor: alpha(theme.palette.neutral1000, 0.6),
          }}
        />

        <Thumbnail
          src={uploadingVideoThumbnailUrl || thumbnailBlobUrl || base64}
          alt="video-thumbnail"
          data-apply-filter={!uploadingVideoThumbnailUrl && !thumbnailBlobUrl}
          style={{
            width: `${calculatedWidth}px`,
            height: `${calculatedHeight}px`,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
          onClick={playVideo}
        />

        <InfoContainer onClick={startDownload}>
          {!isInProgress && state !== 'AVAILABLE' && (
            <DownloadIcon
              sx={{ color: 'neutral000', width: '20px', height: '20px' }}
            />
          )}

          {state === 'AVAILABLE' && (
            <InfoTypography>{videoDurationText}</InfoTypography>
          )}

          {state !== 'AVAILABLE' && (
            <InfoTexts>
              {!isInProgress && (
                <InfoTypography>{videoDurationText}</InfoTypography>
              )}

              {state === 'CONVERTING' ? (
                <InfoTypography sx={{ direction: 'ltr' }} noWrap>
                  {`${t('chat.compressing')} (${toLocale(
                    conversionPercentage,
                  )}%)`}
                </InfoTypography>
              ) : (
                <InfoTypography sx={{ direction: 'ltr' }} noWrap>
                  {isInProgress
                    ? `${progressedSizeText} ${progressSizeUnit} / ${fileSizeText} ${fileSizeUnit}`
                    : `${fileSizeText} ${fileSizeUnit}`}
                </InfoTypography>
              )}
            </InfoTexts>
          )}
        </InfoContainer>
      </Container>

      {statusComp ? (
        <div style={{ padding: '7px 10px' }}>{statusComp}</div>
      ) : null}
    </>
  )
}
